import type { ApiErrorBody, ErrorCode } from '@busbook/shared';

/**
 * Browser API client. One instance per app; it owns token storage and the
 * refresh-on-401 dance so screens never think about tokens.
 */

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: unknown;
  /** Extra top-level fields, e.g. maintenance branding on a 503. */
  readonly extra: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody & Record<string, unknown>) {
    super(body?.error?.message ?? 'Request failed');
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'INTERNAL_ERROR';
    this.details = body?.error?.details;
    const { error: _error, ...rest } = body ?? {};
    this.extra = rest as Record<string, unknown>;
  }
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Where tokens are persisted. Each app uses its own key so they never mix. */
  storageKey: string;
  /** Endpoint used to exchange a refresh token, e.g. "/public/auth/refresh". */
  refreshPath: string;
  onAuthLost?: () => void;
}

export class ApiClient {
  private tokens: Tokens | null = null;
  private refreshing: Promise<Tokens | null> | null = null;

  constructor(private readonly options: ApiClientOptions) {
    this.tokens = readTokens(options.storageKey);
  }

  get accessToken(): string | null {
    return this.tokens?.accessToken ?? null;
  }

  isAuthenticated(): boolean {
    return this.tokens !== null;
  }

  setTokens(tokens: Tokens | null): void {
    this.tokens = tokens;
    writeTokens(this.options.storageKey, tokens);
  }

  async request<T>(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<T> {
    const response = await this.send(path, init);

    if (response.status === 401 && init.auth !== false && this.tokens) {
      const refreshed = await this.refresh();
      if (refreshed) {
        const retry = await this.send(path, init);
        return this.parse<T>(retry);
      }
      this.setTokens(null);
      this.options.onAuthLost?.();
    }

    return this.parse<T>(response);
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  /**
   * A request whose response headers the caller also needs.
   *
   * `request` throws them away, which is right for almost everything — a caller wanting
   * the body should not have to step over transport detail to reach it. This exists for
   * the cases where a header carries meaning the body cannot: whether a service worker
   * served this from cache, and when it was fetched.
   *
   * Deliberately not folded into `request` with a flag: two return shapes behind one
   * signature is how a caller ends up reading `.data` off a body that does not have it.
   */
  async requestDetailed<T>(
    path: string,
    init: RequestInit & { auth?: boolean } = {},
  ): Promise<{ data: T; headers: Headers }> {
    const response = await this.send(path, init);

    if (response.status === 401 && init.auth !== false && this.tokens) {
      const refreshed = await this.refresh();
      if (refreshed) {
        const retry = await this.send(path, init);
        return { data: await this.parse<T>(retry), headers: retry.headers };
      }
      this.setTokens(null);
      this.options.onAuthLost?.();
    }

    return { data: await this.parse<T>(response), headers: response.headers };
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', ...jsonPayload(body) });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', ...jsonPayload(body) });
  }

  /** Partial update. PUT replaces; several endpoints here only ever change named fields. */
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', ...jsonPayload(body) });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  /** Multipart upload (logo). The browser sets the boundary itself. */
  upload<T>(path: string, form: FormData): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: form });
  }

  /**
   * Fetches a binary file and hands it to the browser as a download.
   *
   * A plain link or `window.open` cannot be used: authentication is a Bearer
   * token in storage, not a cookie, so a browser-initiated navigation would
   * arrive unauthenticated and 401.
   */
  async download(path: string, filename: string): Promise<void> {
    const response = await this.send(path, { method: 'GET' });

    if (response.status === 401 && this.tokens) {
      const refreshed = await this.refresh();
      if (!refreshed) {
        this.setTokens(null);
        this.options.onAuthLost?.();
        throw new Error('Session expired');
      }
      return this.download(path, filename);
    }
    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(response.status, (safeJson(text) ?? {}) as ApiErrorBody & Record<string, unknown>);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      // Revoking immediately can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  }

  private send(path: string, init: RequestInit & { auth?: boolean }): Promise<Response> {
    const headers = new Headers(init.headers);
    if (init.auth !== false && this.tokens) {
      headers.set('authorization', `Bearer ${this.tokens.accessToken}`);
    }
    return fetch(`${this.options.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const body = text ? safeJson(text) : null;

    if (!response.ok) {
      throw new ApiError(response.status, (body ?? {}) as ApiErrorBody & Record<string, unknown>);
    }
    return (body as { data: T })?.data as T;
  }

  /** Single-flight refresh: concurrent 401s wait on one exchange. */
  private refresh(): Promise<Tokens | null> {
    this.refreshing ??= (async () => {
      try {
        const response = await fetch(`${this.options.baseUrl}${this.options.refreshPath}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ refreshToken: this.tokens?.refreshToken }),
        });
        if (!response.ok) return null;
        const body = (await response.json()) as { data: { tokens: Tokens } };
        this.setTokens(body.data.tokens);
        return body.data.tokens;
      } catch {
        return null;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function readTokens(key: string): Tokens | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Tokens;
    return parsed?.accessToken && parsed?.refreshToken ? parsed : null;
  } catch {
    return null;
  }
}

function writeTokens(key: string, tokens: Tokens | null): void {
  try {
    if (tokens) localStorage.setItem(key, JSON.stringify(tokens));
    else localStorage.removeItem(key);
  } catch {
    // Private-mode browsers can refuse storage; the session simply won't persist.
  }
}

/**
 * The content type and the body, or neither.
 *
 * A request with nothing to send must not announce `content-type: application/json`, because
 * that header is a promise that a JSON document follows. Fastify holds callers to it and
 * rejects the request with FST_ERR_CTP_EMPTY_JSON_BODY -- "Body cannot be empty when
 * content-type is set to 'application/json'" -- before any route handler runs.
 *
 * These helpers used to set the header unconditionally and the body only when there was one,
 * so every action with nothing to send (`api.post('/platform/demo/seed')`) failed on the
 * header it had sent about a body it had not. The failure names the content type, so it reads
 * like a serialisation problem in the thing being requested rather than a client that
 * described an empty request wrongly.
 *
 * Sending `{}` instead would also have worked, and is worse: it puts a body on the wire that
 * means nothing, and a route that later starts validating its payload would accept it.
 */
function jsonPayload(body: unknown): Pick<RequestInit, 'headers' | 'body'> {
  if (body === undefined) return {};
  return { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}
